import {engineContext} from './context.js';

(function(root,factory){
  const api=factory({
    Core:root.ZooInvestEngineCore||root.ZooInvestEngine||null,
    Portfolio:root.ZooInvestPortfolioEngine||null,
    Market:root.ZooInvestMarketEngine||null,
    Tactical:root.ZooInvestTacticalMarketEngine||null,
    Ecosystem:root.ZooInvestEcosystemEngine||null
  });
  if(typeof module==='object'&&module.exports) module.exports=api;
  root.ZooInvestDecisionOrchestrator=api;
})(engineContext,function(Deps){
  'use strict';
  const VERSION='0.1.0-alpha';
  const CALIBRATED=false;
  const ORDER=Object.freeze(['BUSINESS','STOCK','TIMING','WHY_WRONG']);
  const ALLOWED_LANGUAGE=Object.freeze(['WATCH','INVESTIGATE','REVIEW','ATTRACTIVE_ZONE','RISK_ELEVATED','OPEN','BLOCKED','NO_ACTION']);

  function deepFreeze(v){if(v&&typeof v==='object'&&!Object.isFrozen(v)){Object.freeze(v);Object.values(v).forEach(deepFreeze)}return v}
  function norm(v){return String(v??'').trim().toUpperCase()}
  function result(engine,payload){return deepFreeze({engine,version:VERSION,calibrated:false,...payload})}
  function hasObj(v){return v&&typeof v==='object'}
  function safeCall(fn,args,fallback=null){try{return typeof fn==='function'?fn(...args):fallback}catch(e){return {status:'OPEN',error:String(e&&e.message||e)}}}
  function statusOf(v){return norm(v?.status||v?.state||v?.level||'OPEN')||'OPEN'}
  function scorelessCopy(v){if(!hasObj(v))return v; const o={}; for(const [k,val] of Object.entries(v)){if(k==='investmentScore'||k==='universalScore')continue;o[k]=val}return o}

  function buildVerificationManifest(groups={}){
    const rows=[];
    for(const [section,items] of Object.entries(groups)){
      for(const [name,value] of Object.entries(items||{})){
        const status=statusOf(value);
        rows.push({section,name,status,open:['OPEN','UNVERIFIED','NOT_CONFIGURED','INSUFFICIENT_SAMPLE','INCOMPLETE'].includes(status),blocked:['BLOCKED','FAIL','RED'].includes(status)});
      }
    }
    const open=rows.filter(r=>r.open).length, blocked=rows.filter(r=>r.blocked).length;
    return result('VerificationManifest',{status:blocked?'BLOCKED':open?'PARTIAL':'COMPLETE',openCount:open,blockedCount:blocked,rows});
  }

  function businessSection(core,eco){
    return deepFreeze({
      companyQuality:scorelessCopy(core?.companyQuality||null),
      financialHealth:scorelessCopy(core?.financialHealth||null),
      growthQuality:scorelessCopy(core?.growthQuality||null),
      revenueQuality:scorelessCopy(core?.revenueQuality||null),
      demandEvidence:scorelessCopy(core?.demandEvidence||null),
      trajectory:scorelessCopy(core?.trajectory||null),
      bottleneck:scorelessCopy(core?.bottleneck||null),
      ecosystemConfirmation:scorelessCopy(eco?.ecosystemConfirmation||null),
      endMarketHealth:scorelessCopy(eco?.endMarketHealth||null),
      geographyRisk:scorelessCopy(eco?.geographyRisk||null)
    });
  }

  function stockSection(core,tactical){
    return deepFreeze({
      stockOpportunity:scorelessCopy(core?.stockOpportunity||null),
      expectations:scorelessCopy(core?.expectations||null),
      discovery:scorelessCopy(core?.discovery||null),
      stockCrowding:scorelessCopy(core?.stockCrowding||null),
      sectorCrowding:scorelessCopy(core?.sectorCrowding||null),
      capitalStructure:scorelessCopy(core?.capitalStructure||null),
      priceVolumeConfirmation:scorelessCopy(tactical?.priceVolumeConfirmation||null),
      shortPressure:scorelessCopy(tactical?.shortPressure||null)
    });
  }

  function timingSection(core,market,tactical,portfolio){
    return deepFreeze({
      entryGate:scorelessCopy(core?.entryGate||null),
      catalyst:scorelessCopy(core?.catalyst||null),
      earningsPositioning:scorelessCopy(core?.earningsPositioning||null),
      marketMood:scorelessCopy(market?.marketMood||null),
      regimeShift:scorelessCopy(market?.regimeShift||null),
      horseRegimeFit:scorelessCopy(market?.horseRegimeFit||null),
      horseSwitch:scorelessCopy(market?.horseSwitch||null),
      crossMarketStress:scorelessCopy(tactical?.crossMarketStress||null),
      liquidityOvershoot:scorelessCopy(tactical?.liquidityOvershoot||null),
      reentry:scorelessCopy(portfolio?.reentry||null)
    });
  }

  function whyWrongSection(core,eco,tactical,portfolio){
    return deepFreeze({
      whyAmIWrong:scorelessCopy(core?.whyAmIWrong||null),
      verificationGate:scorelessCopy(core?.verificationGate||null),
      riskEvent:scorelessCopy(core?.riskEvent||null),
      capitalStructure:scorelessCopy(core?.capitalStructure||null),
      ecosystemEarlyWarning:scorelessCopy(eco?.ecosystemEarlyWarning||null),
      monetizationGap:scorelessCopy(eco?.monetizationGap||null),
      geographyRisk:scorelessCopy(eco?.geographyRisk||null),
      reactionDeviation:scorelessCopy(tactical?.reactionDeviation||null),
      portfolioExitChallenge:scorelessCopy(portfolio?.exitChallenge||null)
    });
  }

  function attentionQueue(sections,manifest){
    const q=[];
    function add(section,name,obj,priority,reason){if(obj)q.push({section,name,status:statusOf(obj),priority,reason})}
    const w=sections.WHY_WRONG, t=sections.TIMING, s=sections.STOCK, b=sections.BUSINESS;
    if(manifest.blockedCount)add('WHY_WRONG','Verification blockers',w.verificationGate,1,'Critical verification or risk blockers exist.');
    if(['BLOCKED','FAIL'].includes(statusOf(t.entryGate)))add('TIMING','Entry Gate',t.entryGate,1,'Timing/protection gate is not open.');
    if(['RED','RISK_ELEVATED','HIGH','HIGH_GAP'].includes(statusOf(w.riskEvent))||['HIGH_GAP'].includes(statusOf(w.monetizationGap)))add('WHY_WRONG','Risk review',w.riskEvent||w.monetizationGap,2,'Risk engine requires review.');
    if(['REVIEW_ROTATION'].includes(statusOf(t.horseSwitch)))add('TIMING','Horse Switch',t.horseSwitch,2,'Relative opportunity changed; review rotation.');
    if(['CONFIRMED_DIRECTION'].includes(statusOf(b.ecosystemConfirmation)))add('BUSINESS','Ecosystem confirmation',b.ecosystemConfirmation,3,'Independent ecosystem signals agree.');
    if(['HIGH','ELEVATED','HIGH_CROWDING'].some(x=>statusOf(s.stockCrowding).includes(x)))add('STOCK','Crowding',s.stockCrowding,3,'Crowding can raise expectation/re-rating risk.');
    return deepFreeze(q.sort((a,b)=>a.priority-b.priority));
  }

  function decisionLanguage(sections,manifest){
    if(manifest.blockedCount>0)return 'BLOCKED';
    if(['BLOCKED','FAIL'].includes(statusOf(sections.TIMING.entryGate)))return 'WATCH';
    if(['HIGH_GAP','RED','RISK_ELEVATED'].includes(statusOf(sections.WHY_WRONG.monetizationGap))||statusOf(sections.WHY_WRONG.riskEvent)==='RED')return 'RISK_ELEVATED';
    if(statusOf(sections.TIMING.horseSwitch)==='REVIEW_ROTATION')return 'REVIEW';
    const so=sections.STOCK.stockOpportunity; const disc=sections.STOCK.discovery;
    if((so&&typeof so.score==='number'&&so.score>=7)||(disc&&typeof disc.score==='number'&&disc.score>=7))return 'INVESTIGATE';
    return manifest.openCount?'WATCH':'NO_ACTION';
  }

  function orchestrate(input={}){
    const core=input.coreResult||safeCall(Deps.Core?.evaluateCompany,[input.companySnapshot||{},input.companyOptions||{}],{});
    const market=input.marketResult||safeCall(Deps.Market?.evaluateMarket,[input.marketSnapshot||{}],{});
    const ecosystem=input.ecosystemResult||safeCall(Deps.Ecosystem?.evaluateEcosystem,[input.ecosystemSnapshot||{}],{});
    const portfolio=input.portfolioResult||safeCall(Deps.Portfolio?.evaluatePortfolio,[input.portfolioSnapshot||{}],{});

    let tactical=input.tacticalResult||{};
    if(!input.tacticalResult&&Deps.Tactical){
      const ts=input.tacticalSnapshot||{};
      tactical={
        priceVolumeConfirmation:safeCall(Deps.Tactical.priceVolumeConfirmation,[ts.priceVolume||{}]),
        shortPressure:safeCall(Deps.Tactical.shortPressure,[ts.shortPressure||{}]),
        crossMarketStress:safeCall(Deps.Tactical.crossMarketStress,[ts.crossMarketStress||{}]),
        liquidityOvershoot:safeCall(Deps.Tactical.liquidityOvershoot,[ts.liquidityOvershoot||{}]),
        reactionDeviation:safeCall(Deps.Tactical.reactionDeviation,[ts.reactionDeviation||{}])
      };
    }

    const sections=deepFreeze({
      BUSINESS:businessSection(core,ecosystem),
      STOCK:stockSection(core,tactical),
      TIMING:timingSection(core,market,tactical,portfolio),
      WHY_WRONG:whyWrongSection(core,ecosystem,tactical,portfolio)
    });
    const manifest=buildVerificationManifest(sections);
    const language=decisionLanguage(sections,manifest);
    const attention=attentionQueue(sections,manifest);
    return result('DecisionOrchestrator',{
      order:ORDER,sections,verificationManifest:manifest,attentionQueue:attention,
      decisionLanguage:ALLOWED_LANGUAGE.includes(language)?language:'WATCH',
      universalInvestmentScore:null,
      note:'Research compression only. BUSINESS, STOCK, TIMING and WHY WRONG remain separate; no universal Investment Score and no automatic trade instruction.'
    });
  }

  function selfTest(){
    const base={
      coreResult:{companyQuality:{status:'STRONG',score:8.5},financialHealth:{status:'STRONG',score:8},growthQuality:{status:'PROVISIONAL',score:7.8},revenueQuality:{status:'PROVISIONAL',score:8},demandEvidence:{status:'PROVISIONAL',score:8},trajectory:{status:'PROVISIONAL',score:8},bottleneck:{status:'PROVISIONAL',score:8.5},stockOpportunity:{status:'PROVISIONAL',score:7.4},expectations:{status:'WATCH',risk:7},discovery:{status:'PROVISIONAL',score:6.8},stockCrowding:{status:'ELEVATED',score:7.6},sectorCrowding:{status:'MODERATE',score:5.5},capitalStructure:{status:'LOW',risk:2},entryGate:{status:'PASS',state:'PASS'},catalyst:{status:'OPEN',score:null},earningsPositioning:{status:'WATCH'},whyAmIWrong:{status:'PROVISIONAL'},verificationGate:{status:'VERIFIED'},riskEvent:{status:'MODERATE'}},
      marketResult:{marketMood:{status:'PROVISIONAL',mood:'RISK_ON'},regimeShift:{status:'NO_SHIFT'},horseRegimeFit:{status:'PROVISIONAL'},horseSwitch:{status:'NO_SWITCH'}},
      ecosystemResult:{ecosystemConfirmation:{status:'CONFIRMED_DIRECTION',direction:'POSITIVE'},endMarketHealth:{status:'PROVISIONAL',score:8},geographyRisk:{status:'PROVISIONAL',risk:5},ecosystemEarlyWarning:{status:'OPEN',personalAlert:false},monetizationGap:{status:'CONVERTING'}},
      tacticalResult:{priceVolumeConfirmation:{status:'CONFIRMED_UP'},shortPressure:{status:'LOW'},crossMarketStress:{status:'PROVISIONAL',classification:'MIXED'},liquidityOvershoot:{status:'NO_FLAG'},reactionDeviation:{status:'NORMAL_RANGE'}},
      portfolioResult:{reentry:{status:'OPEN'},exitChallenge:{status:'OPEN'}}
    };
    const r=orchestrate(base);
    const blocked=orchestrate({...base,coreResult:{...base.coreResult,verificationGate:{status:'BLOCKED'},entryGate:{status:'PASS',state:'PASS'}}});
    const entryBlocked=orchestrate({...base,coreResult:{...base.coreResult,entryGate:{status:'BLOCKED',state:'BLOCKED'}}});
    const alteredMarket=orchestrate({...base,marketResult:{...base.marketResult,marketMood:{status:'PROVISIONAL',mood:'RISK_OFF'}}});
    const assertions=[
      ['canonical order is BUSINESS STOCK TIMING WHY WRONG',JSON.stringify(r.order)===JSON.stringify(ORDER)],
      ['no universal Investment Score is created',r.universalInvestmentScore===null&&!('investmentScore' in r)],
      ['company quality stays in BUSINESS only',r.sections.BUSINESS.companyQuality.score===8.5&&r.sections.TIMING.companyQuality===undefined],
      ['stock opportunity stays separate from company quality',r.sections.STOCK.stockOpportunity.score===7.4&&r.sections.BUSINESS.stockOpportunity===undefined],
      ['Entry Gate stays in TIMING',r.sections.TIMING.entryGate.state==='PASS'],
      ['Why Am I Wrong and Verification Gate stay in WHY WRONG',!!r.sections.WHY_WRONG.whyAmIWrong&&!!r.sections.WHY_WRONG.verificationGate],
      ['critical verification blocker can block compressed conclusion',blocked.decisionLanguage==='BLOCKED'],
      ['blocked Entry Gate does not become attractive language',['WATCH','BLOCKED'].includes(entryBlocked.decisionLanguage)],
      ['market changes do not rewrite Company Quality',alteredMarket.sections.BUSINESS.companyQuality.score===r.sections.BUSINESS.companyQuality.score],
      ['ecosystem warning cannot become personal alert through orchestration',r.sections.WHY_WRONG.ecosystemEarlyWarning.personalAlert===false],
      ['decision language excludes BUY and SELL',!['BUY','SELL'].includes(r.decisionLanguage)],
      ['verification manifest exposes open/blocked states',typeof r.verificationManifest.openCount==='number'&&typeof r.verificationManifest.blockedCount==='number'],
      ['attention queue is review context not trade order',r.attentionQueue.every(x=>!['BUY','SELL'].includes(x.status))],
      ['result explicitly preserves separate dimensions',r.note.includes('remain separate')],
      ['scoreless copy strips accidental universal score fields',scorelessCopy({investmentScore:9,x:1}).investmentScore===undefined]
    ];
    return deepFreeze({version:VERSION,passed:assertions.every(([,ok])=>!!ok),tests:assertions.map(([name,ok])=>({name,passed:!!ok}))});
  }

  const API={VERSION,CALIBRATED,ORDER,ALLOWED_LANGUAGE,buildVerificationManifest,businessSection,stockSection,timingSection,whyWrongSection,attentionQueue,decisionLanguage,orchestrate,selfTest};
  try{API.selfTestResult=selfTest();}catch(e){API.bootstrapError=String(e&&e.message||e)}
  return Object.freeze(API);
});

